/**
 * BPMN から「メッセージ起票のモデルか」を判定して、起票に必要な情報を取り出す。
 *
 * 以前はモデル id・message 名・フォーム名・レーン名をフロントのコードに持っていた
 * （`domain/case/scoped-start.ts`）。モデルを増やすたびにフロントの変更とデプロイが
 * 必要で、BPMN と二重管理になっていた。必要な情報はすべて BPMN にあるので読む。
 *
 * 案件種別の詳細画面は図の表示で既に主 BPMN を取得しているので、追加の取得は不要
 * （同じクエリのキャッシュに乗る）。
 */

const BPMN_NS = "http://www.omg.org/spec/BPMN/20100524/MODEL";
const SPIFF_NS = "http://spiffworkflow.org/bpmn/schema/1.0/core";

/**
 * SpiffArena が「起票者に割り当てる」と解釈するレーン名。
 * `process_initiator` / `processInitiator` などに一致する（backend の正規表現と同じ）。
 */
const INITIATOR_LANE = /^process.?initiator$/i;

export type MessageStart = {
  /** 起票時に叩くメッセージ名。 */
  readonly messageName: string;
  /** 起票フォームの JSON Schema ファイル名。宣言が無ければ null。 */
  readonly formSchemaFile: string | null;
  readonly formUiSchemaFile: string | null;
  /** 承認者を解決するレーン名。 */
  readonly approvalLanes: readonly string[];
};

/**
 * メッセージ起票のモデルなら情報を返す。通常の起票経路なら null。
 *
 * `messageRef` が無い場合も null にする。SpiffWorkflow はその形だと payload を
 * 捨てるので、起票しても承認者が渡らない（起票の選択肢に出してはいけない）。
 */
export function parseMessageStart(xml: string): MessageStart | null {
  const document = parseXml(xml);
  if (document === null) return null;

  const startEvent = findMessageStartEvent(document);
  if (startEvent === null) return null;

  const eventDefinition = childByTag(startEvent, "messageEventDefinition");
  const messageRef = eventDefinition?.getAttribute("messageRef") ?? "";
  if (messageRef === "") return null;

  const messageName = messageNameOf(document, messageRef);
  if (messageName === null) return null;

  return {
    messageName,
    formSchemaFile: propertyOf(startEvent, "formJsonSchemaFilename"),
    formUiSchemaFile: propertyOf(startEvent, "formUiSchemaFilename"),
    approvalLanes: approvalLanesOf(document, startEvent),
  };
}

function parseXml(xml: string): Document | null {
  if (xml.trim() === "") return null;

  const document = new DOMParser().parseFromString(xml, "application/xml");
  // パースエラーは例外ではなく parsererror 要素で返る
  if (document.getElementsByTagName("parsererror").length > 0) return null;

  return document;
}

function findMessageStartEvent(document: Document): Element | null {
  const startEvents = [
    ...document.getElementsByTagNameNS(BPMN_NS, "startEvent"),
  ];

  return (
    startEvents.find(
      (element) => childByTag(element, "messageEventDefinition") !== null,
    ) ?? null
  );
}

function childByTag(parent: Element, localName: string): Element | null {
  return (
    [...parent.children].find(
      (child) =>
        child.namespaceURI === BPMN_NS && child.localName === localName,
    ) ?? null
  );
}

function messageNameOf(document: Document, messageRef: string): string | null {
  const message = [...document.getElementsByTagNameNS(BPMN_NS, "message")].find(
    (element) => element.getAttribute("id") === messageRef,
  );
  const name = message?.getAttribute("name") ?? "";

  return name === "" ? null : name;
}

/** 開始イベントに宣言された spiff の property（フォーム名など）。 */
function propertyOf(startEvent: Element, name: string): string | null {
  const properties = [
    ...startEvent.getElementsByTagNameNS(SPIFF_NS, "property"),
  ];
  const found = properties.find(
    (element) => element.getAttribute("name") === name,
  );
  const value = found?.getAttribute("value") ?? "";

  return value === "" ? null : value;
}

/**
 * 承認者を解決するレーン。
 *
 * 人手タスクを含むレーンのうち、開始イベントのあるレーン（システム側）と
 * `process_initiator`（起票者に割り当たる予約名）を除く。差し戻しの修正タスクは
 * `process_initiator` レーンに置く規約なので、ここで自然に外れる。
 */
function approvalLanesOf(
  document: Document,
  startEvent: Element,
): readonly string[] {
  const userTaskIds = new Set(
    [...document.getElementsByTagNameNS(BPMN_NS, "userTask")].map((element) =>
      element.getAttribute("id"),
    ),
  );
  const startEventId = startEvent.getAttribute("id");

  const lanes = [...document.getElementsByTagNameNS(BPMN_NS, "lane")];
  const result: string[] = [];

  for (const lane of lanes) {
    const name = lane.getAttribute("name") ?? "";
    if (name === "" || INITIATOR_LANE.test(name)) continue;

    const refs = [...lane.getElementsByTagNameNS(BPMN_NS, "flowNodeRef")].map(
      (element) => element.textContent?.trim() ?? "",
    );
    if (refs.includes(startEventId ?? "")) continue;
    if (!refs.some((ref) => userTaskIds.has(ref))) continue;

    result.push(name);
  }

  return result;
}
